import { AlertTriangleIcon, MicIcon, X as XIcon } from "lucide-react";
import { Button } from "@component/button";

export type AudioWarningVariant = "weak" | "low" | "noise";

interface AudioWarningTooltipProps {
    variant: AudioWarningVariant;
    onClose: () => void;
    /** Only required for "weak" and "low" variants. Omit for "noise". */
    onOpenSettings?: () => void;
}

/**
 * In-interview audio warning tooltip (ticket #1870 + #1871, PRD §3.2).
 *
 * Shown above the Settings button in the controls bar when the candidate
 * experiences an audio issue mid-interview. Three variants:
 *
 * - "low"   — microphone completely undetected; shows MicIcon + Settings badge.
 * - "weak"  — mic picking up very faint audio; shows MicIcon + Settings badge.
 * - "noise" — sustained loud background noise (environmental, not device);
 *             shows AlertTriangleIcon, NO Settings badge, NO "device settings" link.
 *
 * All three share the same dark popup design used for the "Poor internet
 * connection" tooltip. The tooltip is dismissible via its close button and
 * re-arms whenever the status returns to "strong" and then degrades again.
 */
export default function AudioWarningTooltip({
    variant,
    onClose,
    onOpenSettings,
}: AudioWarningTooltipProps) {
    const isLow = variant === "low";
    const isNoise = variant === "noise";

    // ── Copy ──────────────────────────────────────────────────────────────────
    const header = isLow
        ? "Your microphone cannot be detected."
        : isNoise
        ? "Loud background noise detected."
        : "Weak audio signal detected.";

    // ── Icon ──────────────────────────────────────────────────────────────────
    // Noise is an environmental issue, not a device issue, so we use a
    // different icon to avoid implying the user should check their mic settings.
    const Icon = isNoise ? AlertTriangleIcon : MicIcon;

    // ── Body ──────────────────────────────────────────────────────────────────
    // Noise variant: plain text, no inline "device settings" link.
    // Low / weak variants: text + clickable "device settings" link.
    const settingsLink = !isNoise && onOpenSettings && (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onOpenSettings();
            }}
            className="text-blue-400 hover:text-blue-300 underline cursor-pointer bg-transparent border-none p-0 font-inherit inline"
        >
            device settings
        </button>
    );

    const bodyContent = isNoise ? (
        <span>
            Sounds like your environment is noisy. Find a quieter space for a better
            interview experience.
        </span>
    ) : isLow ? (
        <span>
            The AI interviewer is unable to hear your response. Please check your{" "}
            {settingsLink}.
        </span>
    ) : (
        <span>
            Your microphone may not be picking up your voice clearly. Try checking
            your {settingsLink}.
        </span>
    );

    return (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-50 animate-in slide-in-from-bottom-2 duration-200">
            <div className="bg-gray-800 text-white rounded-lg shadow-2xl p-4 min-w-[420px] max-w-[500px] border border-gray-600">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-2">
                        <div className="flex items-start gap-2">
                            <Icon className="h-4 w-4 mt-0.5 text-yellow-400 flex-shrink-0" />
                            <div className="text-sm text-gray-200">
                                <span className="font-semibold">{header}</span>
                            </div>
                        </div>
                        <div className="text-sm text-gray-300 leading-relaxed">
                            {bodyContent}
                        </div>
                    </div>

                    {/* Close button */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 rounded-full hover:bg-gray-800 text-gray-400 hover:text-gray-200 flex-shrink-0"
                        onClick={onClose}
                    >
                        <XIcon className="h-4 w-4" />
                        <span className="sr-only">Close</span>
                    </Button>
                </div>

                {/* Downward arrow pointing at the Settings button */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                    <div className="border-8 border-transparent border-t-gray-600" />
                </div>
            </div>
        </div>
    );
}
